import { Filter, FilterState, PersonType } from 'models';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';

import { filters } from './filter-options';
import { Count, FilterToggle, List, SubfilterLabel } from './styled';
import { TickIcon } from './tick';
import { useLocale } from 'locale';
import { filterPeople } from './helpers';
import { useDataContext } from 'context/data-context';

type Props = {
  visibleFilter: string;
  activeFilter: FilterState | null;
  person?: PersonType;
  activeSubfilters: FilterState[];
  value?: keyof PersonType;
  onChange: (activeFilter: FilterState | null) => void;
  onSubfilterChange: (activeFilter: FilterState | null) => void;
  onToggle: (activeFilter: Filter) => void;
};

const OptionsList = ({
  visibleFilter,
  person,
  activeFilter,
  subFilters,
  onChange,
  activeCount,
  isSubfilter,
  label,
  value,
}: Props & {
  subFilters?: Filter['options'];
  activeCount: number;
  isSubfilter?: boolean;
  label?: string;
}) => {
  const locale = useLocale();

  return (
    <List as="div" isSubfilter={isSubfilter}>
      <Swiper
        key={visibleFilter}
        slidesPerView="auto"
        draggable
        freeMode
        navigation
        modules={[Navigation]}
      >
        {!!label && (
          <SwiperSlide className="label-slide">
            <SubfilterLabel>{label}:</SubfilterLabel>
          </SwiperSlide>
        )}
        {(person
          ? subFilters?.filter(
              (s) =>
                (value ? person[value] : activeFilter?.value) === s.value ||
                (value ? person[value] : activeFilter?.value) ===
                  locale.localize(s.labelUz, s.labelRu)
            )
          : subFilters
        )?.map((o) => {
          const isActive =
            activeFilter &&
            (activeFilter.value === o.value ||
              activeFilter?.value === locale.localize(o.labelUz, o.labelRu))
              ? true
              : undefined;
          return (
            <SwiperSlide key={o.value || locale.localize(o.labelUz, o.labelRu)}>
              <span>
                <FilterToggle
                  isSubfilter={isSubfilter}
                  role="button"
                  tabIndex={0}
                  data-active={isActive}
                  onClick={() => {
                    onChange({
                      name: visibleFilter as keyof PersonType,
                      value: (o.value || locale.localize(o.labelUz, o.labelRu)) as string,
                    });
                  }}
                >
                  {locale.localize(o.labelUz, o.labelRu) || o.label}
                  {isActive && <Count>{activeCount}</Count>}
                </FilterToggle>
              </span>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </List>
  );
};

export const Filters = (props: Props) => {
  const locale = useLocale();
  const { people } = useDataContext();
  const {
    visibleFilter,
    activeFilter,
    onChange,
    onSubfilterChange,
    onToggle,
    person,
    activeSubfilters,
  } = props;
  const subFilters = filters.find((f) => f.value === visibleFilter)?.options;
  const activeFilterObject = activeFilter
    ? filters.find((f) => f.value === activeFilter?.name) || null
    : null;

  return (
    <>
      <List isPrimary as="div">
        <Swiper slidesPerView="auto" draggable freeMode navigation modules={[Navigation]}>
          {!person && (
            <SwiperSlide>
              <FilterToggle
                role="button"
                tabIndex={0}
                data-active={!activeFilter?.value ? true : undefined}
                onClick={() => {
                  onChange(null);
                }}
              >
                {!activeFilter && <TickIcon />} {locale.home.allVictims}
              </FilterToggle>
            </SwiperSlide>
          )}

          {filters.map((f) => (
            <SwiperSlide key={f.value}>
              <FilterToggle
                role="button"
                tabIndex={0}
                data-active={visibleFilter === f.value ? true : undefined}
                onClick={() => {
                  onToggle(f);
                  !!person &&
                    onChange({
                      name: f.value as keyof PersonType,
                      value: person[(f.value ?? '') as keyof PersonType] as string,
                    });
                }}
              >
                {activeFilter?.name === f.value && <TickIcon />}{' '}
                {locale.localize(f.labelUz, f.labelRu) || f.label}
              </FilterToggle>
            </SwiperSlide>
          ))}
        </Swiper>
      </List>

      <OptionsList
        {...props}
        subFilters={subFilters}
        activeCount={
          activeFilter?.value ? filterPeople(people, activeFilter, [], person?.id).length : 0
        }
      />

      {!!activeFilterObject &&
        activeFilterObject.value === visibleFilter &&
        activeFilterObject?.subfilters?.map((subfilter, index) => (
          <OptionsList
            key={subfilter.value}
            {...props}
            visibleFilter={subfilter.value}
            activeFilter={activeSubfilters?.find((s) => s.name === subfilter.value) || null}
            activeCount={
              filterPeople(
                people,
                activeFilter,
                index === 0 && activeSubfilters.find((s) => s.name === subfilter.value)
                  ? [activeSubfilters.find((s) => s.name === subfilter.value) as FilterState]
                  : activeSubfilters,
                person?.id
              ).length
            }
            onChange={onSubfilterChange}
            subFilters={subfilter.options}
            value={subfilter.value}
            label={locale.localize(subfilter.labelUz, subfilter.labelRu) || subfilter.label}
            isSubfilter
          />
        ))}
    </>
  );
};
